import { Fragment, useState } from 'react';
import { Button, Chip, Dialog } from '@nyce/admin-kit/ui';
import { ErrorBox, TableScroller, TagsField } from '../components/common';
import { errorMessage } from '../lib/errors';
import { byProductionFirst } from '../identity/session';
import {
  billing,
  type BillingColumn,
  type BillingReportInfo,
  type BillingResult,
  type BillingRun,
  type BillingTarget,
  type Values,
} from './api/client';
import './billing.css';

const INT = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 });
const MONEY = new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const formatValue = (v: number | undefined, format: BillingColumn['format']) =>
  (format === 'money' ? MONEY : INT).format(v ?? 0);

/** yyyy-MM of the month before `now` — the default period. */
export function previousMonth(now = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** The report's environments, production first (the default). */
export const targetsOf = (r: BillingReportInfo | undefined): BillingTarget[] =>
  byProductionFirst(r?.targets ?? []);

export const reportTitle = (reports: BillingReportInfo[] | undefined, id: string) =>
  reports?.find((r) => r.id === id)?.title ?? id;

export const targetLabel = (reports: BillingReportInfo[] | undefined, reportId: string, target: string) =>
  reports?.find((r) => r.id === reportId)?.targets.find((t) => t.id === target)?.label ?? target;

export function RunStatusChip({ run }: { run: Pick<BillingRun, 'status' | 'sendError' | 'sentAt'> }) {
  const [tone, label] =
    run.status === 'failed'
      ? ['red', 'Failed']
      : run.status === 'running'
        ? ['grey', 'Running']
        : run.sendError
          ? ['amber', 'Not sent']
          : run.sentAt
            ? ['green', 'Sent']
            : ['green', 'Ready'];
  return (
    <Chip size="sm" className={`oph-status nyk-tone-${tone}`} data-status={label.toLowerCase()}>
      {label}
    </Chip>
  );
}

/**
 * The report's customers: billed first (each with its chat/agent lines and their sum), the billed total, then the
 * internal customers apart. `columns` limits what is shown (the unit always first); the files carry every column.
 */
export function ResultTable({ result, compact }: { result: BillingResult; compact?: boolean }) {
  const unit = result.columns.find((c) => c.key === result.unitKey)!;
  const rest = result.columns.filter((c) => c.key !== result.unitKey);
  const columns = [
    unit,
    ...(compact ? rest.filter((c) => ['questions', 'users', 'estimatedCost'].includes(c.key)) : rest),
  ];
  const billed = result.customers.filter((c) => !c.internal);
  const internal = result.customers.filter((c) => c.internal);
  const cells = (values: Values) =>
    columns.map((c) => (
      <td key={c.key} data-label={c.label} className={`bl-num${c.key === result.unitKey ? ' bl-unit' : ''}`}>
        {formatValue(values[c.key], c.format)}
      </td>
    ));
  const head = (
    <thead>
      <tr>
        <th>{result.customerLabel}</th>
        <th>Line</th>
        {columns.map((c) => (
          <th key={c.key} className="bl-num">
            {c.label}
          </th>
        ))}
      </tr>
    </thead>
  );
  const section = (list: typeof billed, total: Values, totalLabel: string, testId: string) => (
    <TableScroller>
      <table className="nyk-ui-table nyk-cards bl-result" data-testid={testId}>
        {head}
        {list.map((c) => (
          <tbody key={c.id || c.name} className="nyk-row-group" data-customer={c.id}>
            {c.lines.map((l, i) => (
              <tr key={l.label}>
                <td className="nyk-card-title">
                  {i === 0 ? c.name : <span className="bl-repeat">{c.name}</span>}
                </td>
                <td data-label="Line">{l.label}</td>
                {cells(l.values)}
              </tr>
            ))}
            {c.lines.length > 1 && (
              <tr className="bl-sum">
                <td className="nyk-card-title">
                  <span className="bl-repeat">{c.name}</span>
                </td>
                <td data-label="Line">Sum</td>
                {cells(c.total)}
              </tr>
            )}
          </tbody>
        ))}
        <tbody className="bl-total">
          <tr>
            <td className="nyk-card-title">{totalLabel}</td>
            <td data-label="Line" />
            {cells(total)}
          </tr>
        </tbody>
      </table>
    </TableScroller>
  );
  return (
    <div className="bl-results">
      {billed.length ? (
        section(billed, result.total, 'Total billed', 'billing-billed')
      ) : (
        <p className="nyk-ui-hint">No billable usage in this period.</p>
      )}
      {internal.length > 0 && (
        <>
          <h3 className="bl-subhead">Internal (not billed)</h3>
          {section(internal, result.internalTotal, 'Total internal', 'billing-internal')}
        </>
      )}
    </div>
  );
}

/** Per installation, as on the Excel detail sheet. */
export function DetailTable({ result }: { result: BillingResult }) {
  const unit = result.columns.find((c) => c.key === result.unitKey)!;
  const extra = result.columns.filter((c) => ['questions', 'users'].includes(c.key));
  return (
    <TableScroller>
      <table className="nyk-ui-table nyk-cards bl-result" data-testid="billing-detail">
        <thead>
          <tr>
            <th>{result.detailLabel}</th>
            <th>{result.customerLabel}</th>
            <th>Line</th>
            {[unit, ...extra].map((c) => (
              <th key={c.key} className="bl-num">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {result.detail.map((d, i) => (
            <Fragment key={`${d.customerId}/${d.installation}/${d.line}/${i}`}>
              <tr className={d.internal ? 'nyk-row-muted' : undefined}>
                <td className="nyk-card-title">{d.installation}</td>
                <td data-label={result.customerLabel}>
                  {d.customer}
                  {d.internal && <span className="nyk-ui-hint"> · internal</span>}
                </td>
                <td data-label="Line">{d.line}</td>
                {[unit, ...extra].map((c) => (
                  <td key={c.key} data-label={c.label} className="bl-num">
                    {formatValue(d.values[c.key], c.format)}
                  </td>
                ))}
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
    </TableScroller>
  );
}

/** Mails a stored run (PDF + Excel) to the given addresses. */
export function SendDialog({
  run,
  initial,
  onSent,
  onClose,
}: {
  run: Pick<BillingRun, 'id' | 'periodLabel'> & { title: string };
  initial?: string[];
  onSent: (r: BillingRun) => void;
  onClose: () => void;
}) {
  const [recipients, setRecipients] = useState<string[]>(initial ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bad = recipients.filter((r) => !EMAIL.test(r));
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      onSent(await billing.send(run.id, recipients));
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Send ${run.title} — ${run.periodLabel}`}
      maxWidth={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={!recipients.length || bad.length > 0} onClick={() => void send()}>
            Send
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      <TagsField
        label="Recipients"
        value={recipients}
        onChange={setRecipients}
        placeholder="name@example.com"
        hint={
          bad.length
            ? `not an e-mail address: ${bad.join(', ')}`
            : 'PDF and Excel attached, the billed totals in the mail.'
        }
      />
    </Dialog>
  );
}

export const EMAIL = /^[^\s@<>,;"]+@[^\s@<>,;"]+\.[^\s@<>,;"]+$/;
